import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Search, Users, FileText, ChevronRight, Tag, ChevronDown, X as XIcon } from "lucide-react";
import VisitsHistoryTable from "@/components/VisitsHistoryTable";
import PatientProfile from "@/components/PatientProfile";
import { serviceLabel } from "@/components/NewVisitModal";

function PatientRow({ p, onOpen }) {
  const initials = `${(p.patient_name || " ")[0] || ""}${(p.patient_surname || " ")[0] || ""}`.toUpperCase();
  return (
    <button
      data-testid={`patient-row-${(p.patient_fiscal_code || `${p.patient_name}-${p.patient_surname}`).replace(/\s+/g, "_")}`}
      onClick={onOpen}
      className="w-full bg-white rounded-xl border border-[#E2E8F0] p-4 hover:border-brand-azure hover:shadow-sm transition text-left flex items-center gap-3"
    >
      <div className="w-11 h-11 rounded-full bg-brand-navy/10 text-brand-navy flex items-center justify-center font-extrabold shrink-0">{initials}</div>
      <div className="min-w-0 flex-1">
        <div className="font-extrabold text-brand-navy truncate">{p.patient_surname} {p.patient_name}</div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
          {p.patient_birth && <span>{p.patient_birth}</span>}
          {p.patient_fiscal_code && <span className="font-mono">{p.patient_fiscal_code}</span>}
          {p.patient_phone && <span>{p.patient_phone}</span>}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5">
          {p.visits_count > 0
            ? <>Ultima visita: {p.last_visit_at ? new Date(p.last_visit_at).toLocaleDateString("it-IT") : "—"} {p.last_visit_service ? `· ${serviceLabel(p.last_visit_service)}` : ""} · {p.visits_count} chius{p.visits_count === 1 ? "a" : "e"}</>
            : <>Nessuna visita chiusa{p.has_open_draft ? " · bozza aperta" : ""}</>}
        </div>
      </div>
      {p.has_open_draft && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full shrink-0">Bozza aperta</span>}
      <ChevronRight size={16} className="text-slate-300 shrink-0" />
    </button>
  );
}

export default function Pazienti() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "visite" ? "visite" : "pazienti";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [tagQ, setTagQ] = useState(searchParams.get("tag") || "");
  const [allTags, setAllTags] = useState([]);
  const [tagListOpen, setTagListOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // Deep-link sync anche a componente già montato (azioni assistente / navigazione interna)
  useEffect(() => {
    const pq = searchParams.get("q");
    const pt = searchParams.get("tag");
    if (pq !== null) setQ(pq);
    if (pt !== null) setTagQ(pt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (tab !== "pazienti") return;
    const t = setTimeout(() => {
      api.get("/medico/patients", { params: { q: q || undefined, tag: tagQ || undefined, limit: 100 } })
        .then(({ data }) => setItems(data || []))
        .catch(() => setItems([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, tagQ, tab]);

  // Tendina Tag paziente esistenti (unici, con conteggio pazienti)
  useEffect(() => {
    if (tab !== "pazienti") return;
    api.get("/medico/patient-tags/suggestions")
      .then(({ data }) => setAllTags(data?.items || []))
      .catch(() => setAllTags([]));
  }, [tab]);

  const setTab = (t) => setSearchParams(t === "visite" ? { tab: "visite" } : {}, { replace: true });

  return (
    <AppShell title="Pazienti" subtitle="Anagrafiche, profili completi e storico visite">
      <div className="flex items-center gap-2 mb-5" data-testid="pazienti-tabs">
        <button data-testid="tab-pazienti" onClick={() => setTab("pazienti")}
          className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 border-2 transition ${tab === "pazienti" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure"}`}>
          <Users size={14} /> Pazienti
        </button>
        <button data-testid="tab-tutte-visite" onClick={() => setTab("visite")}
          className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 border-2 transition ${tab === "visite" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure"}`}>
          <FileText size={14} /> Tutte le visite
        </button>
      </div>

      {tab === "visite" ? (
        <VisitsHistoryTable />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-3 items-start">
            <div className="relative flex-1 min-w-[260px] max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                data-testid="patient-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per nome, cognome, CF, telefono o email…"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:border-brand-azure"
              />
            </div>
            {/* Ricerca per Tag paziente (normalizzata: con/senza #, maiuscole/minuscole) */}
            <div className="relative w-full sm:w-72" data-testid="patient-tag-search-box">
              <Tag className="absolute left-3 top-[13px] text-slate-400" size={16} />
              <input
                data-testid="patient-tag-search"
                value={tagQ}
                onChange={(e) => setTagQ(e.target.value)}
                onFocus={() => setTagListOpen(true)}
                placeholder="Ricerca per tag (es. pterigio o #pterigio)…"
                className="w-full pl-10 pr-16 py-2.5 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:border-brand-azure"
              />
              <div className="absolute right-2 top-[9px] flex items-center gap-1">
                {tagQ && (
                  <button data-testid="patient-tag-clear" onClick={() => setTagQ("")} title="Pulisci filtro tag"
                    className="p-1 rounded hover:bg-slate-100 text-slate-400"><XIcon size={14} /></button>
                )}
                <button data-testid="patient-tag-list-toggle" onClick={() => setTagListOpen((x) => !x)} title="Mostra tag esistenti"
                  className="p-1 rounded hover:bg-slate-100 text-slate-500">
                  <ChevronDown size={16} className={`transition-transform ${tagListOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
              {tagListOpen && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-[#E2E8F0] rounded-lg shadow-lg overflow-hidden" data-testid="patient-tag-dropdown">
                  <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 flex items-center justify-between">
                    <span>Tag paziente esistenti ({allTags.length})</span>
                    <button onClick={() => setTagListOpen(false)} className="text-slate-400 hover:text-slate-600"><XIcon size={12} /></button>
                  </div>
                  <div className="max-h-56 overflow-y-auto overscroll-contain" data-testid="patient-tag-dropdown-list">
                    {allTags.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-slate-400 italic">Nessun tag paziente salvato</div>
                    ) : allTags.map((t) => (
                      <button
                        key={t.tag}
                        data-testid={`patient-tag-option-${t.tag.replace("#", "")}`}
                        onClick={() => { setTagQ(t.tag); setTagListOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-brand-azure/10 transition ${tagQ === t.tag ? "bg-brand-azure/10 font-bold text-brand-navy" : "text-slate-700"}`}
                      >
                        <span className="font-semibold">{t.tag}</span>
                        <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">{t.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {tagQ && (
            <div className="mb-3 flex items-center gap-2 text-xs" data-testid="patient-tag-active-filter">
              <span className="text-slate-500">Filtro tag attivo:</span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-azure text-white font-bold">
                {tagQ.startsWith("#") ? tagQ : `#${tagQ}`}
                <button onClick={() => setTagQ("")} data-testid="patient-tag-filter-remove" className="hover:text-brand-orange"><XIcon size={11} /></button>
              </span>
            </div>
          )}

          {items === null ? (
            <div className="text-slate-500 py-8 text-center">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center" data-testid="patients-empty">
              <Users className="mx-auto text-slate-300 mb-3" size={36} />
              <div className="font-bold text-brand-navy">Nessun paziente trovato</div>
              <div className="text-sm text-slate-500">{tagQ ? "Nessun paziente con questo tag. Controlla la grafia nella tendina dei tag esistenti." : "I pazienti compaiono qui dopo la prima visita o prenotazione."}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="patients-grid">
              {items.map((p, i) => (
                <PatientRow key={`${p.patient_fiscal_code || ""}-${p.patient_name}-${p.patient_surname}-${i}`} p={p} onOpen={() => setSelected(p)} />
              ))}
            </div>
          )}
        </>
      )}

      {selected && <PatientProfile patient={selected} onClose={() => setSelected(null)} />}
    </AppShell>
  );
}
